import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePlayerCtx } from "@/context/PlayerContext";
import { ConfidenceBadge } from "@/design";

const G = "#00C896";

export function PlayerBar() {
  const p = usePlayerCtx();
  const nav = useNavigate();
  const [expanded, setExpanded] = useState(false);
  if (!p || (!p.playingId && p.status !== "failed")) return null;
  const st = p.station || {};
  // A YouTube-live source is a `youtube_live` embed handed back by the
  // resolver — the browser <audio> tag can't play it, but a YouTube iframe
  // can. We mount the iframe inline so playback continues across SPA routes.
  const isYouTubeLive = !!p.embedUrl && /youtube\.com\/embed\//.test(p.embedUrl);
  const isEmbed = p.status === "embed";
  const reconnecting = p.status === "reconnecting" || p.status === "connecting";
  const failed = p.status === "failed";
  // Zero-vs-unknown: unknown renders "—", never 0. Measured 0 renders 0.
  const countText = p.liveCount ? p.liveCount.people.toLocaleString() : "—";
  const countLabel = p.liveCount ? p.liveCount.label : null;
  // Clean YouTube watch URL from the embed URL — used for the "Open on YouTube"
  // fallback when YouTube's own domain policy blocks iframe embedding.
  const ytVideoId = p.embedUrl?.match(/embed\/([^?/]+)/)?.[1];
  const ytWatchUrl = ytVideoId ? `https://www.youtube.com/watch?v=${ytVideoId}` : null;

  return (
    <div data-testid="player-bar" style={{
      position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 300,
      background: "#0A1628", borderTop: `1px solid ${G}55`,
      paddingBottom: "env(safe-area-inset-bottom, 0px)",
      boxShadow: "0 -10px 40px rgba(0,0,0,0.45)", color: "#E2E8F0",
      fontFamily: "'Inter',system-ui,sans-serif" }}>
      {/* YouTube-live inline iframe — 160×90 pip inside the docked player. */}
      {isYouTubeLive && (
        <div data-testid="player-youtube-iframe-wrap" style={{
          padding: "10px 16px 0", display: "flex", justifyContent: "flex-end",
          gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <a data-testid="player-yt-open"
            href={ytWatchUrl || "#"}
            target="_blank" rel="noopener noreferrer"
            style={{
              color: "#94A3B8", fontSize: 11, textDecoration: "none",
              padding: "6px 10px", border: "1px solid #1E293B",
              borderRadius: 6, whiteSpace: "nowrap",
            }}>
            Open on YouTube ↗
          </a>
          <iframe
            data-testid="player-youtube-iframe"
            src={p.embedUrl}
            title={`${st.name || "Station"} — live on YouTube`}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            style={{
              width: expanded ? 320 : 160, height: expanded ? 180 : 90,
              border: "none", borderRadius: 8,
              transition: "width .25s ease, height .25s ease",
              background: "#000",
            }}
          />
        </div>
      )}
      {expanded && (
        <div data-testid="player-expanded" style={{ padding: "18px 20px 6px", borderBottom: "1px solid #1E293B",
          display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ width: 56, height: 56, borderRadius: 12, background: "#13233F", display: "flex",
            alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 800, color: G }}>
            {st.logo_url ? <img src={st.logo_url} alt="" style={{ width: "100%", height: "100%", borderRadius: 12, objectFit: "cover" }} /> : (st.name || "?")[0]}
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 800, fontSize: 16 }}>{st.name}{st.frequency ? ` ${st.frequency}` : ""}</div>
            <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 4, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span>{countText} listening</span>
              {countLabel && <ConfidenceBadge source={countLabel} size="xs" tone="dark" />}
              {p.liveCount?.as_of && <span>as of {new Date(p.liveCount.as_of).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} GMT</span>}
            </div>
          </div>
          <button data-testid="player-share" onClick={() => {
              navigator.clipboard?.writeText(`${window.location.origin}/stations/${st.id}?play=1`);
            }}
            style={btn()}>Share</button>
          <button data-testid="player-details" onClick={() => { setExpanded(false); nav(`/stations/${st.id}`); }}
            style={btn()}>Station details →</button>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", minHeight: 64 }}>
        <div data-testid="player-expand" onClick={() => setExpanded((e) => !e)}
          style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, cursor: "pointer", minWidth: 0 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: "#13233F", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, color: G }}>
            {(st.name || "?")[0]}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {st.name}{st.frequency ? ` · ${st.frequency}` : ""}
            </div>
            <div style={{ fontSize: 11, color: "#94A3B8", display: "flex", gap: 8, alignItems: "center" }}>
              {failed ? (
                <span style={{ color: "#F59E0B" }}>Stream unavailable — try again shortly</span>
              ) : reconnecting ? (
                <span data-testid="player-reconnecting" style={{ color: "#F59E0B" }}>
                  <span style={{ display: "inline-block", animation: "wsSpin 1s linear infinite" }}>◌</span> {p.status === "connecting" ? "Connecting…" : "Reconnecting…"}
                </span>
              ) : (
                <span style={{ color: G }}>● LIVE</span>
              )}
              <span data-testid="player-count">
                {p.liveCount ? `${countText} listening` : "— listening · checking"}
              </span>
              {countLabel && (
                <span data-testid="player-badge" onClick={(e) => { e.stopPropagation(); nav("/methodology"); }}>
                  <ConfidenceBadge source={countLabel} size="xs" tone="dark" />
                </span>
              )}
            </div>
          </div>
        </div>
        {isYouTubeLive ? (
          <button data-testid="player-yt-stop" onClick={() => p.stop()} style={playBtn()}>
            ❚❚
          </button>
        ) : isEmbed ? (
          <button data-testid="player-watch-on-page" onClick={() => nav(`/stations/${st.id}`)} style={btn(G)}>
            Watch on station page →
          </button>
        ) : failed ? (
          <button data-testid="player-play" onClick={() => p.toggle(st)} style={playBtn()}>▶</button>
        ) : (
          <button data-testid="player-play" aria-label={p.playingId ? "Pause" : "Play"}
            onClick={() => p.toggle(st)} style={playBtn()}>
            {p.playingId ? "❚❚" : "▶"}
          </button>
        )}
        <input type="range" min="0" max="1" step="0.05" defaultValue="1"
          data-testid="player-volume" aria-label="Volume"
          onChange={(e) => p.setVolume(parseFloat(e.target.value))}
          style={{ width: 90, accentColor: G }} className="ws-player-volume" />
        <button data-testid="player-close" aria-label="Close player" onClick={() => p.stop()}
          style={{ ...btn(), padding: "6px 10px" }}>✕</button>
      </div>
      <style>{`@keyframes wsSpin{to{transform:rotate(360deg)}}
        @media (max-width:640px){ .ws-player-volume{display:none} }`}</style>
    </div>
  );
}

function btn(color = "#94A3B8") {
  return { background: "transparent", border: "1px solid #1E293B", color,
    padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" };
}
function playBtn() {
  return { background: G, border: "none", color: "#04141F", width: 44, height: 44,
    borderRadius: "50%", cursor: "pointer", fontSize: 15, fontWeight: 800, flexShrink: 0 };
}
